import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import ResumeUploadModal from './ResumeUploadModal';
import Footer from '@/Company/About/Footer';
import Navbar from '@/Company/About/Navbar';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from '@/components/ui/dropdown-menu';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Heading, Text } from '@/components/ui/typography';
import { Stack } from '@/components/ui/layout';
import { Separator } from '@/components/ui/separator';
import { Container } from '@/components/ui/container';
import { ChevronDown, X, Upload, FileText, Loader2, Sparkles, AlertTriangle, CheckCircle2, Menu, FileCheck, CheckCircle, ArrowLeft, TrendingUp, Lightbulb, Award } from 'lucide-react';


const ResumeTemplates = ({ onTemplateSelect, parsedData, onBack }) => {
  const [hoveredTemplate, setHoveredTemplate] = useState(null);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showJdAlignModal, setShowJdAlignModal] = useState(false);
  
  // JD Align Resume states - Same as ResumeLandingPage.jsx
  const [jdFile, setJdFile] = useState(null);
  const [jdText, setJdText] = useState('');
  const [jdInputMethod, setJdInputMethod] = useState('file');
  const [useExistingResume, setUseExistingResume] = useState(true);
  const [uploadedResumeFile, setUploadedResumeFile] = useState(null);
  const [jdAlignStep, setJdAlignStep] = useState('options');
  const [tailoredResumeData, setTailoredResumeData] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState('');
  
  // JD Analysis states - Same as ResumeLandingPage.jsx
  const [showAnalysisPopup, setShowAnalysisPopup] = useState(false);
  const [jdAnalysis, setJdAnalysis] = useState(null);
  const [jdData, setJdData] = useState(null);
  const [resumeData, setResumeData] = useState(null);
  
  const navigate = useNavigate();
  
  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);


  // Handle Resume Upload - Same as ResumeLandingPage.jsx
  const handleUploadResume = (parsedData) => {
    // Store parsed data for template selection
    localStorage.setItem('parsedResumeData', JSON.stringify(parsedData));
    setShowUploadModal(false);
    // Stay on templates page - user should select a template
    // Don't automatically open any template
  };

  // Handle Create New Resume - Same as ResumeLandingPage.jsx
  const handleCreateNew = () => {
    setShowUploadModal(false);
    // Stay on templates page - user should select a template
    // Don't clear data - templates will show default data when no parsed data available
    // Don't automatically open any template
  };

  // Handle Close Modal - Same as ResumeLandingPage.jsx
  const handleCloseModal = () => {
    setShowUploadModal(false);
  };

  // Handle Close JD Align Modal - Same as ResumeLandingPage.jsx
  const handleCloseJdAlignModal = () => {
    setShowJdAlignModal(false);
    setJdAlignStep('options');
    setError('');
    setIsProcessing(false);
  };

  // JD Align Resume handlers - Same as ResumeLandingPage.jsx
  const handleJdFileChange = (event) => {
    const file = event.target.files[0];
    setJdFile(file);
    setError('');
  };

  const handleResumeFileChange = (event) => {
    const file = event.target.files[0];
    setUploadedResumeFile(file);
    setError('');
  };

  // Get existing resume data from localStorage - Same as ResumeLandingPage.jsx
  const getExistingResumeData = () => {
    try {
      const savedData = localStorage.getItem('resumeData');
      if (savedData) {
        return JSON.parse(savedData);
      }
    } catch (error) {
      // Error reading saved resume data
    }
    return null;
  };

  // Save resume data to localStorage - Same as ResumeLandingPage.jsx
  const saveResumeData = (data) => {
    try {
      localStorage.setItem('resumeData', JSON.stringify(data));
    } catch (error) {
      // Error saving resume data
    }
  };

  // Check if backend is running - Same as ResumeLandingPage.jsx
  const checkBackendHealth = async () => {
    try {
      const response = await fetch('/api/health', {
        method: 'GET',
        mode: 'cors',
        signal: AbortSignal.timeout(5000) // 5 second timeout
      });
      return response.ok;
    } catch (error) {
      return false;
    }
  };

  // Process new resume file - Same as ResumeLandingPage.jsx
  const processNewResume = async (file) => {
    const formData = new FormData();
    formData.append('resume', file);

    const response = await fetch('/api/parse-resume', {
      method: 'POST',
      body: formData,
      mode: 'cors',
    });

    if (!response.ok) {
      throw new Error('Failed to process resume file');
    }

    const data = await response.json();
    return data;
  };

  // Clean HTML tags - Same as ResumeLandingPage.jsx
  const cleanHtmlTags = (text) => {
    if (typeof text !== 'string') return text;
    return text.replace(/<[^>]*>/g, '');
  };

  // Process tailored data to match our form structure - Same as ResumeLandingPage.jsx
  const processTailoredData = (data) => {
    // Handle the backend response structure properly
    const personalInfo = data.personal_info || {};
    const atsSections = data.ats_sections || {};

    const sanitizeText = (value) => {
      if (typeof value === 'string') {
        return value.trim();
      }
      if (value === null || value === undefined) {
        return '';
      }
      return String(value).trim();
    };

    const sanitizeArray = (items) => {
      if (!Array.isArray(items)) return [];
      return items
        .map((item) => sanitizeText(item))
        .filter((item) => item.length > 0);
    };

    const highlightSections = Array.isArray(atsSections.highlight_sections)
      ? atsSections.highlight_sections
          .map((section) => ({
            title: sanitizeText(section?.title),
            items: sanitizeArray(section?.items),
          }))
          .filter((section) => section.items.length > 0)
      : [];

    return {
      template_name: "professional",
      isTemplate: false,
      // Personal Information - handle both nested and flat structures
      full_name: personalInfo.full_name || data.full_name || "",
      email: personalInfo.email || data.email || "",
      phone: personalInfo.phone || data.phone || "",
      location: personalInfo.location || data.location || "",
      github: personalInfo.github || data.github || "",
      linkedin: personalInfo.linkedin || atsSections.linkedin || data.linkedin || "",
      leetcode: personalInfo.leetcode || data.leetcode || "",
      hackerrank: personalInfo.hackerrank || data.hackerrank || "",
      whatsapp: atsSections.whatsapp || personalInfo.whatsapp || "",
      
      // Professional Summary (AI optimized for JD)
      professional_summary: cleanHtmlTags(data.professional_summary || data.summary || data.resume_summary || ""),
      summary_intro: cleanHtmlTags(atsSections.summary_intro || data.professional_summary || data.summary || data.resume_summary || ""),
      looking_to_items: sanitizeArray(atsSections.looking_to_items),
      expertise_paragraph: cleanHtmlTags(atsSections.expertise_paragraph || ""),
      collaboration_paragraph: cleanHtmlTags(atsSections.collaboration_paragraph || ""),
      call_to_action_phone: sanitizeText(atsSections.call_to_action_phone),
      call_to_action_email: sanitizeText(atsSections.call_to_action_email),
      
      // Skills (prioritized based on JD requirements) - ensure proper comma formatting
      skills: {
        programming_languages: Array.isArray(data.skills?.programming_languages) ? data.skills.programming_languages : 
          (data.skills?.programming_languages || data.skills?.technical_skills || data.programming_languages || "").toString().split(/[,]/).map(s => s.trim()).filter(s => s),
        tools: Array.isArray(data.skills?.tools) ? data.skills.tools : 
          (data.skills?.tools || data.skills?.technologies || data.tools || "").toString().split(/[,]/).map(s => s.trim()).filter(s => s),
        frameworks: Array.isArray(data.skills?.frameworks) ? data.skills.frameworks : 
          (data.skills?.frameworks || data.frameworks || "").toString().split(/[,]/).map(s => s.trim()).filter(s => s),
        ai_ml_technologies: Array.isArray(data.skills?.ai_ml_technologies) ? data.skills.ai_ml_technologies : 
          (data.skills?.ai_ml_technologies || data.skills?.ai_technologies || data.ai_ml_technologies || "").toString().split(/[,]/).map(s => s.trim()).filter(s => s)
      },
      key_skills: Array.isArray(data.skills?.programming_languages) ? data.skills.programming_languages : 
        (data.skills?.programming_languages || data.skills?.technical_skills || data.programming_languages || "").toString().split(/[,]/).map(s => s.trim()).filter(s => s),
      technical_skills: Array.isArray(data.skills?.tools) ? data.skills.tools : 
        (data.skills?.tools || data.skills?.technologies || data.tools || "").toString().split(/[,]/).map(s => s.trim()).filter(s => s),
      key_bullet_sections: highlightSections.length
        ? highlightSections
        : (() => {
            const fallbackItems = (
              Array.isArray(data.skills?.programming_languages)
                ? data.skills.programming_languages
                : (data.skills?.programming_languages || data.skills?.technical_skills || data.programming_languages || "")
                    .toString()
                    .split(/[,]/)
                    .map((s) => s.trim())
                    .filter((s) => s)
            ).slice(0, 8);
            return fallbackItems.length
              ? [
                  {
                    title: "Key Highlights",
                    items: fallbackItems,
                  },
                ]
              : [];
          })(),
      
      // Experience (AI optimized for JD relevance)
      relevant_experience: Array.isArray(data.relevant_experience) ? data.relevant_experience : 
        (Array.isArray(data.experience) ? data.experience : []),
      experience: Array.isArray(data.experience) ? data.experience : 
        (Array.isArray(data.relevant_experience) ? data.relevant_experience : []),
      
      // Education
      education: Array.isArray(data.education) ? data.education : [],
      
      // Projects (AI optimized for JD relevance)
      projects: Array.isArray(data.projects) ? data.projects : [],
      
      // Other fields
      achievements: Array.isArray(data.achievements) ? data.achievements : [],
      certifications: Array.isArray(data.certifications) ? data.certifications : [],
      languages: Array.isArray(data.languages) ? data.languages : [],
      interests: Array.isArray(data.interests) ? data.interests : []
    };
  };

  // JD Align Submit handler - Same as ResumeLandingPage.jsx
  const handleJdAlignSubmit = async () => {
    if (!jdFile && !jdText) {
      setError('Please upload a JD file or enter JD text.');
      return;
    }

    if (!useExistingResume && !uploadedResumeFile) {
      setError('Please select an existing resume or upload a new one.');
      return;
    }

    setIsProcessing(true);
    setError('');

    try {
      // Check if backend is running first
      const backendHealthy = await checkBackendHealth();
      if (!backendHealthy) {
        throw new Error('Backend server is not running. Please start the backend server on port 5001.');
      }

      let resumeData = null;

      if (useExistingResume) {
        // Use existing resume data from localStorage
        resumeData = getExistingResumeData();
        if (!resumeData) {
          throw new Error('No existing resume data found. Please upload a resume first.');
        }
      } else {
        // Process new resume file
        const newResumeData = await processNewResume(uploadedResumeFile);
        resumeData = newResumeData;
        // Save the new resume data
        saveResumeData(newResumeData);
      }

      // Ensure experience data is properly mapped before sending
      const processedResumeData = {
        ...resumeData,
        // Map experience to relevant_experience if it exists
        relevant_experience: resumeData?.relevant_experience || resumeData?.experience || [],
        // Ensure other fields are properly mapped
        experience: resumeData?.experience || resumeData?.relevant_experience || []
      };

      // Call backend to get AI analysis
      const formData = new FormData();
      
      // Add JD file or text
      if (jdFile) {
        formData.append('jd', jdFile);
      } else if (jdText) {
        formData.append('jd_text', jdText);
      }
      
      // Add resume data
      formData.append('resume_data', JSON.stringify(processedResumeData));
      
      // Call backend API to get analysis
      const response = await fetch('/api/create-tailored-resume', {
        method: 'POST',
        body: formData,
        mode: 'cors',
      });

      if (!response.ok) {
        const text = await response.text();
        let msg = `HTTP ${response.status}: ${response.statusText}`;
        try {
          const json = JSON.parse(text);
          if (json?.error) msg += ` – ${json.error}`;
        } catch {}
        throw new Error(msg);
      }

      const tailoredData = await response.json();
      
      // Check if backend provided analysis
      if (tailoredData && tailoredData.jd_analysis) {
        // Use the AI analysis directly from backend
        const aiAnalysis = tailoredData.jd_analysis;
        
        // Convert backend format to frontend display format
        const frontendAnalysis = {
          matchScore: aiAnalysis.match_score || 0,
          matchedSkills: aiAnalysis.matched_skills || [],
          missingSkills: aiAnalysis.missing_skills || [],
          recommendations: aiAnalysis.recommendations || [],
          overallMatch: aiAnalysis.match_level || 'poor',
          totalJdSkills: aiAnalysis.total_jd_skills || 0,
          totalResumeSkills: aiAnalysis.total_resume_skills || 0,
          skillAnalysis: aiAnalysis.skill_analysis || {},
          detailedAnalysis: aiAnalysis.detailed_analysis || {}
        };
        
        // Store data for analysis popup
        setResumeData(processedResumeData);
        setJdAnalysis(frontendAnalysis);
        
        // Show analysis popup
        setShowAnalysisPopup(true);
      } else {
        setError('Backend analysis failed - please try again');
        setJdAlignStep('options');
        return;
      }
      
      setIsProcessing(false);
      
    } catch (error) {
      // Provide helpful error messages
      let errorMessage = error.message;
      if (error.message.includes('Failed to fetch')) {
        errorMessage = 'Cannot connect to backend server. Please ensure the backend is running on port 5001.';
      } else if (error.message.includes('Backend error: 500')) {
        errorMessage = 'Backend server error. Please check the backend logs and try again.';
      } else if (error.message.includes('Backend error: 400')) {
        errorMessage = 'Invalid request. Please check your JD file format and try again.';
      }
      
      setError(errorMessage);
      setJdAlignStep('options');
    } finally {
      setIsProcessing(false);
    }
  };

  // Generate tailored resume after analysis - Same as ResumeLandingPage.jsx
  const generateTailoredResume = async () => {
    setIsProcessing(true);
    setJdAlignStep('processing');

    try {
      const formData = new FormData();
      
      // Add JD file or text
      if (jdFile) {
        formData.append('jd', jdFile);
      } else if (jdText) {
        formData.append('jd_text', jdText);
      }
      
      // Add resume data
      formData.append('resume_data', JSON.stringify(resumeData));
      
      // Call backend API to generate tailored resume
      const response = await fetch('/api/create-tailored-resume', {
        method: 'POST',
        body: formData,
        mode: 'cors',
      });

      if (!response.ok) {
        const text = await response.text();
        let msg = `HTTP ${response.status}: ${response.statusText}`;
        try {
          const json = JSON.parse(text);
          if (json?.error) msg += ` – ${json.error}`;
        } catch {}
        throw new Error(msg);
      }

      const tailoredData = await response.json();
      
      // Persist so Templates and Editor can read it
      localStorage.setItem('tailoredResumeData', JSON.stringify(tailoredData));
      localStorage.setItem('isJDOptimized', 'true');
      
      // Process and map the tailored data
      const processedData = processTailoredData(tailoredData);
      setTailoredResumeData(processedData);
      
      // Close modals and store tailored data
      handleCloseJdAlignModal();
      setShowAnalysisPopup(false);
      
      // Store the tailored data for template selection
      // Don't automatically open any template - user should select one
      // The templates page will use this data when user clicks a template
      
    } catch (error) {
      setError(error.message || 'Failed to generate tailored resume. Please try again.');
      setJdAlignStep('analysis');
    } finally {
      setIsProcessing(false);
    }
  };


  // Check if user is authenticated
  const isAuthenticated = localStorage.getItem("user_email") || localStorage.getItem("company_email");

  // Check for tailored resume data from localStorage
  const getTailoredResumeData = () => {
    try {
      const tailoredData = localStorage.getItem('tailoredResumeData');
      if (tailoredData) {
        const data = JSON.parse(tailoredData);
        localStorage.removeItem('tailoredResumeData'); // Clear after use
        localStorage.removeItem('isJDOptimized'); // Clear flag
        return data;
      }
    } catch (error) {
      // Error reading tailored resume data
    }
    return null;
  };

  // Check if we have JD-optimized data
  const isJDOptimized = () => {
    return localStorage.getItem('isJDOptimized') === 'true';
  };

  const handleTemplateSelect = (templateId) => {
    // Check for tailored resume data first (from JD Align)
    const tailoredData = getTailoredResumeData();
    
    // Check for parsed resume data from localStorage (from Tools -> Resume Templates)
    let parsedResumeData = null;
    try {
      const savedParsedData = localStorage.getItem('parsedResumeData');
      if (savedParsedData) {
        parsedResumeData = JSON.parse(savedParsedData);
      }
    } catch (error) {
      // Error reading parsed resume data
    }
    
    // Priority: tailored data > parsed data from localStorage > original parsedData prop
    const dataToUse = tailoredData || parsedResumeData || parsedData;
    
    // Pass both template ID and data to the parent
    onTemplateSelect(templateId, dataToUse);
  };

  const templates = [
    {
      id: "professional",
      name: "Professional Standard",
      description: "A classic, ATS-friendly template perfect for corporate and business roles.",
      image: "https://qtrypzzcjebvfcihiynt.supabase.co/storage/v1/object/public/base44-prod/public/3823ce2af_professional.jpg",
      category: "Professional",
      featured: true
    },
    {
      id: "simple",
      name: "Simple Clean",
      description: "A minimalist template that highlights your content clearly and professionally.",
      image: "https://qtrypzzcjebvfcihiynt.supabase.co/storage/v1/object/public/base44-prod/public/2b0794d41_simple.jpg",
      category: "Minimal",
      featured: false
    },
    {
      id: "modern",
      name: "Modern Creative",
      description: "A contemporary design with sidebar layout, perfect for creative and tech professionals.",
      image: "https://qtrypzzcjebvfcihiynt.supabase.co/storage/v1/object/public/base44-prod/public/6591613ef_moderns.jpg",
      category: "Modern",
      featured: true
    },
    {
      id:"tech-resume",
      name:"Tech Resume",
      description:"A sleek, modern template designed for tech professionals and developers.",
      image:"./premium.jpeg",
      category:"Technology",
      featured:true
    },
    {
      id: "marketing-specialist",
      name: "Marketing Specialist",
      description: "Perfect for marketing professionals with a clean, organized layout that highlights achievements.",
      image: "https://qtrypzzcjebvfcihiynt.supabase.co/storage/v1/object/public/base44-prod/public/f27141459_Iwillgetyouthejobofyourdreamsthroughexpertcvresumecoverletterwriting.jpg",
      category: "Marketing",
      featured: true
    },
    {
      id:"english-teacher",
      name:"english-teacher",
      description:"A dedicated template for English teachers showcasing their skills and experience.",
      image:"./teacher.jpeg",
      category:"Teacher",
      featured:true
    },
    {
      id:"consulting-profile",
      name:"Consulting Profile",
      description:"Professional consulting template with dark theme, perfect for consultants and business professionals.",
      image:"./infoys.png",
      category:"Consulting",
      featured:true
    },
    {
      id:"tcs",
      name:"TCS Candidate Screening",
      description:"TCS candidate screening form template with structured layout for candidate details, interview availability, skills matrix, and supplier comments.",
      image:"./tcses.png",
      category:"Professional",
      featured:true
    },
    {
      id:"ats",
      name:"ATS Leadership Profile",
      description:"Executive one-page ATS profile tailored for AI leadership with deep-dive accomplishments and training highlights.",
      image:"./harsha.png",
      category:"ATS",
      featured:true
    }
  ];

  // ✅ Filter templates based on user email domain
  // Get user email from localStorage
  const userEmail = localStorage.getItem("user_email") || localStorage.getItem("company_email") || "";
  
  // Check if user has access to consulting template
  const canAccessConsulting = userEmail && (
    userEmail.toLowerCase().endsWith("@newzenalpha.com") || 
    userEmail.toLowerCase().endsWith("@searchjob.ai")
  );

  // Filter templates - hide consulting-profile if user doesn't have access
  const filteredTemplates = canAccessConsulting 
    ? templates  // Show all templates for authorized users
    : templates.filter(t => t.id !== "consulting-profile");  // Hide consulting template for others

  return (
    <div className="min-h-screen bg-background text-foreground w-full">
      <Navbar />
      
      {/* Top Feature Bar - Compact */}
      <div className="bg-primary text-primary-foreground py-2 border-b border-primary/20">
        <Container>
          <div className="flex items-center justify-center gap-4 flex-wrap text-xs font-medium">
            <div className="flex items-center gap-1.5">
              <FileText className="h-3.5 w-3.5" />
              <span>Professional Templates</span>
            </div>
            <Separator orientation="vertical" className="h-3 bg-primary-foreground/30" />
            <div className="flex items-center gap-1.5">
              <CheckCircle className="h-3.5 w-3.5" />
              <span>ATS-Optimized</span>
            </div>
            <Separator orientation="vertical" className="h-3 bg-primary-foreground/30" />
            <div className="flex items-center gap-1.5">
              <Sparkles className="h-3.5 w-3.5" />
              <span>AI-Powered</span>
            </div>
          </div>
        </Container>
      </div>

      {/* Back Button - Shadcn design, minimal padding */}
      <Container>
        <div className="relative py-2">
          <Button
            variant="ghost"
            onClick={onBack}
            size="sm"
            className="h-8 px-3 text-muted-foreground hover:text-foreground"
          >
            <ArrowLeft className="h-4 w-4" />
            Back
          </Button>
        </div>
      </Container>

      {/* Hero Section */}
      <div className="bg-gradient-to-br from-primary/10 via-primary/5 to-background py-8 md:py-12 border-b border-border">
        <Container>
          <div className="max-w-3xl mx-auto space-y-6">
            {/* Hero Content */}
            <div className="text-center space-y-6">
              {isJDOptimized() && (
                <Badge variant="default" className="mb-2">
                  JD-Optimized Resume Ready
                </Badge>
              )}
              <Heading level="h1" className="text-4xl md:text-5xl lg:text-6xl font-bold">
                Professional Resume Templates
              </Heading>
              <Text className="text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto">
                {isJDOptimized() ? 
                  "🎯 Your JD-optimized resume is ready! Choose a template to customize it." :
                  parsedData ? 
                  "Your resume has been parsed! Choose a template to customize it." : 
                  "Choose professionally designed templates to land your dream job"
                }
              </Text>
              <Badge variant="secondary" className="text-sm">
                <Sparkles className="w-4 h-4 mr-2" />
                Over 15,000 professionals hired
              </Badge>
            </div>
          </div>
        </Container>
      </div>

      {/* Templates Grid - Modern 3-column grid using Tailwind */}
      <Container size="2xl" className="py-12 md:py-16 px-4 md:px-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 md:gap-10">
          {filteredTemplates.map((template) => (
            <Card
              key={template.id}
              className={cn(
                "group cursor-pointer overflow-hidden transition-all duration-300 hover:shadow-2xl hover:-translate-y-2",
                "border border-border bg-card rounded-xl",
                hoveredTemplate === template.id && "ring-2 ring-primary ring-offset-4"
              )}
              onClick={() => handleTemplateSelect(template.id)}
              onMouseEnter={() => setHoveredTemplate(template.id)}
              onMouseLeave={() => setHoveredTemplate(null)}
            >
              <div className="relative aspect-[3/4] overflow-hidden bg-muted/20">
                <img
                  src={template.image}
                  alt={template.name}
                  className={cn(
                    "h-full w-full object-cover object-top transition-transform duration-300",
                    "group-hover:scale-105"
                  )}
                />

                {/* Customize Button - appears on hover */}
                <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300 z-20">
                  <Button 
                    size="lg"
                    className="bg-primary text-primary-foreground hover:bg-primary/90 shadow-xl"
                  >
                    Customize This Template
                  </Button>
                </div>

                {/* Badges */}
                {template.featured && (
                  <Badge 
                    variant="default" 
                    className="absolute top-4 left-4 z-10 shadow-lg"
                  >
                    <Sparkles className="w-3.5 h-3.5 mr-1.5" />
                    Featured
                  </Badge>
                )}
                <Badge 
                  variant="secondary" 
                  className="absolute top-4 right-4 z-10 shadow-lg"
                >
                  {template.category}
                </Badge>
              </div>

              <CardContent className="p-6 space-y-2">
                <Heading level="h3" className="text-xl font-semibold line-clamp-1">
                  {template.name}
                </Heading>
                <Text variant="muted" className="text-sm leading-relaxed line-clamp-3">
                  {template.description}
                </Text>
              </CardContent>
            </Card>
          ))}
        </div>
      </Container>

      {/* Footer */}
      <div className="mt-16">
        <Footer />
      </div>

      {/* Resume Templates Modal - Using shadcn Dialog */}
      {showUploadModal && (
        <ResumeUploadModal 
          onUpload={handleUploadResume}
          onCreateNew={handleCreateNew}
          onClose={handleCloseModal}
        />
      )}

      {/* JD Align Resume Modal - Using shadcn Dialog */}
      <Dialog open={showJdAlignModal} onOpenChange={(open) => {
        if (!open) {
          handleCloseJdAlignModal();
        }
      }}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Tailor Resume to JD</DialogTitle>
            <Text variant="muted" className="mt-2">
              Generate a customized resume that highlights your skills and experience to match the job requirements. Our AI analyzes the job description and optimizes your resume for better ATS compatibility and higher match scores.
            </Text>
          </DialogHeader>
          
          {jdAlignStep === 'options' && (
            <Stack spacing="lg">
              {error && (
                <Alert variant="destructive">
                  <AlertTriangle className="h-4 w-4" />
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}
              
              <Stack spacing="lg">
                {/* Option 1: JD Upload */}
                <Card>
                  <CardContent className="p-6">
                    <Stack spacing="md">
                      <Heading level="h3">1. Job Description Input</Heading>
                      
                      {/* Toggle between file and text input */}
                      <RadioGroup 
                        value={jdInputMethod} 
                        onValueChange={setJdInputMethod}
                        className="flex flex-row gap-4"
                      >
                        <div className="flex items-center space-x-2">
                          <RadioGroupItem value="file" id="jd-file-radio" />
                          <Label htmlFor="jd-file-radio" className="flex items-center gap-2 cursor-pointer">
                            <FileText className="w-4 h-4" />
                            Upload File
                          </Label>
                        </div>
                        <div className="flex items-center space-x-2">
                          <RadioGroupItem value="text" id="jd-text-radio" />
                          <Label htmlFor="jd-text-radio" className="flex items-center gap-2 cursor-pointer">
                            <FileText className="w-4 h-4" />
                            Enter Text
                          </Label>
                        </div>
                      </RadioGroup>
                      
                      {/* File Upload Option */}
                      {jdInputMethod === 'file' && (
                        <div>
                          <Input
                            type="file"
                            id="jd-file"
                            accept=".pdf,.docx,.txt"
                            onChange={handleJdFileChange}
                            className="hidden"
                          />
                          <Button
                            variant="outline"
                            className="w-full"
                            onClick={() => document.getElementById('jd-file').click()}
                          >
                            {jdFile ? (
                              <>
                                <FileText className="mr-2 h-4 w-4" />
                                {jdFile.name}
                              </>
                            ) : (
                              <>
                                <Upload className="mr-2 h-4 w-4" />
                                Upload File
                              </>
                            )}
                          </Button>
                          <Text variant="muted" className="text-xs mt-2">
                            Supported formats: PDF, DOCX, TXT
                          </Text>
                        </div>
                      )}
                      
                      {/* Text Input Option */}
                      {jdInputMethod === 'text' && (
                        <div>
                          <Textarea
                            rows={5}
                            placeholder="Paste or type your job description here..."
                            value={jdText}
                            onChange={(e) => setJdText(e.target.value)}
                          />
                          <Text variant="muted" className="text-xs mt-2">
                            Enter the job description text manually.
                          </Text>
                        </div>
                      )}
                    </Stack>
                  </CardContent>
                </Card>

                {/* Option 2: Use Existing Resume */}
                <Card>
                  <CardContent className="p-6">
                    <Stack spacing="md">
                      <Heading level="h3">2. Resume Data Source</Heading>
                      <RadioGroup 
                        value={useExistingResume ? "existing" : "new"} 
                        onValueChange={(value) => setUseExistingResume(value === "existing")}
                        className="flex flex-col gap-2"
                      >
                        <div className="flex items-center space-x-2">
                          <RadioGroupItem value="existing" id="resume-existing" />
                          <Label htmlFor="resume-existing" className="flex items-center gap-2 cursor-pointer">
                            <CheckCircle2 className="w-4 h-4" />
                            Use Existing Resume Data
                          </Label>
                        </div>
                        <div className="flex items-center space-x-2">
                          <RadioGroupItem value="new" id="resume-new" />
                          <Label htmlFor="resume-new" className="flex items-center gap-2 cursor-pointer">
                            <X className="w-4 h-4" />
                            Upload New Resume
                          </Label>
                        </div>
                      </RadioGroup>
                      {useExistingResume && (
                        <Text variant="muted" className="text-sm">
                          {getExistingResumeData() ? 
                            '✅ Resume data found from previous upload' : 
                            '⚠️ No existing resume data found. Please upload a resume first.'}
                        </Text>
                      )}
                    </Stack>
                  </CardContent>
                </Card>

                {/* Option 3: Upload Resume (conditional) */}
                {!useExistingResume && (
                  <Card>
                    <CardContent className="p-6">
                      <Stack spacing="md">
                        <Heading level="h3">3. Upload Resume</Heading>
                        <Input
                          type="file"
                          id="resume-file"
                          accept=".pdf,.docx,.txt"
                          onChange={handleResumeFileChange}
                          className="hidden"
                        />
                        <Button
                          variant="outline"
                          className="w-full"
                          onClick={() => document.getElementById('resume-file').click()}
                        >
                          {uploadedResumeFile ? (
                            <>
                              <FileText className="mr-2 h-4 w-4" />
                              {uploadedResumeFile.name}
                            </>
                          ) : (
                            <>
                              <Upload className="mr-2 h-4 w-4" />
                              Click to upload resume
                            </>
                          )}
                        </Button>
                        <Text variant="muted" className="text-xs">
                          Supported formats: PDF, DOCX, TXT
                        </Text>
                      </Stack>
                    </CardContent>
                  </Card>
                )}

                {/* Submit Button */}
                <DialogFooter>
                  <Button 
                    size="lg"
                    onClick={handleJdAlignSubmit}
                    disabled={(jdInputMethod === 'file' && !jdFile) || (jdInputMethod === 'text' && !jdText.trim()) || (!useExistingResume && !uploadedResumeFile) || isProcessing}
                  >
                    {isProcessing ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Processing...
                      </>
                    ) : (
                      'Generate Tailored Resume'
                    )}
                  </Button>
                </DialogFooter>
              </Stack>
            </Stack>
          )}

          {jdAlignStep === 'processing' && (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <Heading level="h2" className="mb-4">Tailoring Your Resume</Heading>
              <Loader2 className="w-8 h-8 animate-spin mb-4 text-primary" />
              <Text className="text-muted-foreground">Analyzing the job description and optimizing your resume to highlight the most relevant skills and experience...</Text>
            </div>
          )}
        </DialogContent>
      </Dialog>
      
      {/* JD Analysis Popup Modal - Using shadcn Dialog */}
      <Dialog open={showAnalysisPopup} onOpenChange={(open) => {
        if (!open) {
          setShowAnalysisPopup(false);
          setJdAlignStep('options');
        }
      }}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Resume Match Analysis</DialogTitle>
            <Text variant="muted" className="mt-2 text-sm">
              See how well your resume aligns with the job requirements and discover areas to improve.
            </Text>
          </DialogHeader>
          
          {jdAnalysis && (
            <Stack spacing="md">
              {/* Simple Score Display */}
              <div className="flex flex-col items-center justify-center text-center mb-6">
                <div className="relative w-32 h-32 rounded-full bg-primary/10 flex items-center justify-center mb-4">
                  <div className="absolute inset-0 rounded-full border-4 border-primary/20"></div>
                  <div className="flex flex-col items-center justify-center z-10">
                    <span className="text-3xl font-bold text-primary">{jdAnalysis.matchScore || 0}%</span>
                    <span className="text-sm text-muted-foreground">Match Score</span>
                  </div>
                </div>
                <div className="mt-2">
                  <Badge 
                    variant={jdAnalysis.overallMatch === 'excellent' ? 'success' : 
                            jdAnalysis.overallMatch === 'good' ? 'info' : 
                            jdAnalysis.overallMatch === 'fair' ? 'warning' : 'error'}
                    className="text-sm px-4 py-1"
                  >
                    {jdAnalysis.overallMatch ? jdAnalysis.overallMatch.toUpperCase() : 'POOR'}
                  </Badge>
                </div>
              </div>

              {/* Skills Summary */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Skills You Have Section */}
                <Card>
                  <CardContent className="p-4">
                    <Stack spacing="sm">
                      <Heading level="h4" className="text-sm font-semibold">
                        Matching Skills ({jdAnalysis.matchedSkills.length})
                      </Heading>
                      <div className="flex flex-wrap gap-2">
                        {jdAnalysis.matchedSkills.length > 0 ? (
                          jdAnalysis.matchedSkills.map((skill, index) => (
                            <Badge key={index} variant="success" className="text-xs">
                              {skill}
                            </Badge>
                          ))
                        ) : (
                          <Text variant="muted" className="text-xs">No matching skills found</Text>
                        )}
                      </div>
                    </Stack>
                  </CardContent>
                </Card>

                {/* Skills You Need Section */}
                <Card>
                  <CardContent className="p-4">
                    <Stack spacing="sm">
                      <Heading level="h4" className="text-sm font-semibold">
                        Missing Skills ({jdAnalysis.missingSkills.length})
                      </Heading>
                      <div className="flex flex-wrap gap-2">
                        {jdAnalysis.missingSkills.length > 0 ? (
                          jdAnalysis.missingSkills.map((skill, index) => (
                            <Badge key={index} variant="error" className="text-xs">
                              {skill}
                            </Badge>
                          ))
                        ) : (
                          <Text variant="muted" className="text-xs">All required skills are covered!</Text>
                        )}
                      </div>
                    </Stack>
                  </CardContent>
                </Card>
              </div>

              {/* Detailed Analysis */}
              {jdAnalysis.detailedAnalysis && (
                <>
                  {/* Strengths and Areas for Improvement in Two Columns */}
                  {(jdAnalysis.detailedAnalysis.strengths?.length > 0 || jdAnalysis.detailedAnalysis.gaps?.length > 0) && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {jdAnalysis.detailedAnalysis.strengths && jdAnalysis.detailedAnalysis.strengths.length > 0 && (
                        <Card>
                          <CardContent className="p-4">
                            <Stack spacing="sm">
                              <Heading level="h4" className="text-sm font-semibold flex items-center gap-2">
                                <TrendingUp className="h-4 w-4 text-primary" />
                                Strengths
                              </Heading>
                              <ul className="list-disc list-inside space-y-1.5">
                                {jdAnalysis.detailedAnalysis.strengths.map((strength, index) => (
                                  <li key={index}><Text className="text-xs text-muted-foreground">{strength}</Text></li>
                                ))}
                              </ul>
                            </Stack>
                          </CardContent>
                        </Card>
                      )}
                      
                      {jdAnalysis.detailedAnalysis.gaps && jdAnalysis.detailedAnalysis.gaps.length > 0 && (
                        <Card>
                          <CardContent className="p-4">
                            <Stack spacing="sm">
                              <Heading level="h4" className="text-sm font-semibold flex items-center gap-2">
                                <Lightbulb className="h-4 w-4 text-warning" />
                                Areas to Improve
                              </Heading>
                              <ul className="list-disc list-inside space-y-1.5">
                                {jdAnalysis.detailedAnalysis.gaps.map((gap, index) => (
                                  <li key={index}><Text className="text-xs text-muted-foreground">{gap}</Text></li>
                                ))}
                              </ul>
                            </Stack>
                          </CardContent>
                        </Card>
                      )}
                    </div>
                  )}
                </>
              )}

              {/* Recommendations and Focus Areas in Two Columns */}
              {(jdAnalysis.recommendations.length > 0 || (jdAnalysis.detailedAnalysis?.improvement_areas && jdAnalysis.detailedAnalysis.improvement_areas.length > 0)) && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Recommendations */}
                  {jdAnalysis.recommendations.length > 0 && (
                    <Card>
                      <CardContent className="p-4">
                        <Stack spacing="sm">
                          <Heading level="h4" className="text-sm font-semibold flex items-center gap-2">
                            <Lightbulb className="h-4 w-4 text-primary" />
                            Recommendations
                          </Heading>
                          <ul className="list-disc list-inside space-y-1.5">
                            {jdAnalysis.recommendations.slice(0, 3).map((rec, index) => (
                              <li key={index}><Text className="text-xs text-muted-foreground">{rec}</Text></li>
                            ))}
                          </ul>
                        </Stack>
                      </CardContent>
                    </Card>
                  )}
                  
                  {/* Focus Areas */}
                  {jdAnalysis.detailedAnalysis?.improvement_areas && jdAnalysis.detailedAnalysis.improvement_areas.length > 0 && (
                    <Card>
                      <CardContent className="p-4">
                        <Stack spacing="sm">
                          <Heading level="h4" className="text-sm font-semibold flex items-center gap-2">
                            <Award className="h-4 w-4 text-primary" />
                            Focus Areas
                          </Heading>
                          <ul className="list-disc list-inside space-y-1.5">
                            {jdAnalysis.detailedAnalysis.improvement_areas.map((area, index) => (
                              <li key={index}><Text className="text-xs text-muted-foreground">{area}</Text></li>
                            ))}
                          </ul>
                        </Stack>
                      </CardContent>
                    </Card>
                  )}
                </div>
              )}
            </Stack>
          )}

          {/* Action Buttons */}
          <DialogFooter>
            <Button 
              variant="outline"
              onClick={() => {
                setShowAnalysisPopup(false);
                setJdAlignStep('options');
              }}
            >
              Cancel
            </Button>
            <Button 
              size="lg"
              onClick={generateTailoredResume}
              disabled={isProcessing}
            >
              {isProcessing ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Generating...
                </>
              ) : (
                'Get Tailored Resume'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ResumeTemplates;